import React from "react";
import { withStyles } from "@material-ui/core/styles";
import {
  Grid,
  Typography,
  Paper,
  CardMedia,
  Fab,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@material-ui/core";
import { DateTimePicker, MuiPickersUtilsProvider } from "@material-ui/pickers";
// Still used by the makers-stats (Wellness Report) client-side export;
// only the DETAILED report moved to the backend pipeline.
import ExcelJS from "exceljs";
import moment from "moment";
import MomentUtils from "@date-io/moment";
import Add from "@material-ui/icons/Add";
import { useApolloClient } from "@apollo/client";
import EventMakerCard from "./EventMakerCard";
import EventMakerDialog from "./EventMakerDialog";
import SnackbarContext from "../../../containers/CustomSnackbar/SnackbarContext";
import reportsApi from "../../../utils/reportsApi";
import logo from "../../../Assets/logo.png";
import eventMakersV2Query from "../../../graphql/reports/event-makers-v2";
import CurrentCompanyContext from "../../../containers/CurrentCompany/CurrentCompanyContext";
import GET_EVENTS_USERS_STATISTICS from "../../../graphql/event/query/events-users-statistics";
import {
  formatRawDataForExcel,
  generateMonthlyDataFromAggregated,
  generateComparisonData,
  aggregateStatsFromRawData,
  normalizeEventUserStats,
} from "../../../utils/eventStatisticsHelper";

const styles = (theme) => ({
  root: {
    width: "100%",
    overflowX: "auto",
  },
  empty: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    margin: "8px 8px",
    height: 450,
    flex: 1,
  },
  emptyText: {
    fontWeight: 400,
    color: "#75D6B2",
    marginTop: 16,
  },
  header: {
    display: "flex",
    textAlign: "center",
    width: "100%",
    marginBottom: 32,
    justifyContent: "center",
    position: "relative",
    flexWrap: "wrap",
  },
});

const EventMaker = ({ classes }) => {
  const client = useApolloClient();
  const { currentCompany } = React.useContext(CurrentCompanyContext);
  const { openSnackbar } = React.useContext(SnackbarContext);
  const [eventMakers, setEventMakers] = React.useState([]);
  const [statsDialogOpen, setStatsDialogOpen] = React.useState(false);
  const [statsExportDates, setStatsExportDates] = React.useState({
    start: moment().subtract(1, "month"),
    end: moment(),
  });
  const [detailedReportDialogOpen, setDetailedReportDialogOpen] =
    React.useState(false);
  // Detailed report now runs on the backend (POST /api/v2/reports/detailed);
  // the UI only starts the job, polls status and opens the presigned URL.
  const [detailedGenerating, setDetailedGenerating] = React.useState(false);
  const [detailedProgress, setDetailedProgress] = React.useState(null);
  const [editEvent, setEditEvent] = React.useState();
  const [analyticsSite, setAnalyticsSite] = React.useState("All Sites");
  const [detailedSite, setDetailedSite] = React.useState("All Sites");

  React.useEffect(() => {
    if (!currentCompany) return;
    const subscription = client
      .watchQuery({
        // eventMakersV2 filters by company server-side; v1 fetched EVERY
        // company's recurring events and filtered them here instead.
        query: eventMakersV2Query,
        variables: { companyId: currentCompany._id },
      })
      .subscribe(
        ({ data }) => {
          const { eventMakersV2 } = data || {};
          if (eventMakersV2) {
            setEventMakers(eventMakersV2);
          }
        },
        (error) => {
          console.log(error);
        },
      );

    return () => {
      subscription.unsubscribe();
    };
  }, [currentCompany]);

  // ---------- helpers: month range & sample data (same columns as your original) ----------
  const getMonthsInRange = (startDate, endDate) => {
    const months = [];
    let current = startDate.clone().startOf("month");
    const end = endDate.clone().endOf("month");
    while (current.isSameOrBefore(end)) {
      months.push({
        key: current.format("YYYY-MM"),
        name: current.format("MMMM YYYY"),
        shortName: current.format("MMM YYYY"),
        moment: current.clone(),
      });
      current.add(1, "month");
    }
    return months;
  };

  // Removed generateMonthlyData - now using generateMonthlyDataFromAggregated from helper
  // Removed generateComparisonData - now using it from helper

  const generateMonthlyDataFallback = (month) => {
    return {
      summary: [
        ["Metric", "Value", "vs Previous Month"],
        ["Unique Participants", Math.floor(Math.random() * 50) + 150, "+12%"],
        ["Total Check-ins", Math.floor(Math.random() * 150) + 400, "+8%"],
        ["Total Sessions", Math.floor(Math.random() * 20) + 65, "+15%"],
        ["Average Frequency", 2.7, "+0.1"],
        ["Regular Participants", Math.floor(Math.random() * 30) + 85, "+18%"],
      ],
      weeklyTrends: [
        ["Week", "Sessions", "Attendance", "Avg per Session"],
        [
          `Week 1`,
          Math.floor(Math.random() * 30) + 80,
          Math.floor(Math.random() * 100) + 200,
          Math.floor(Math.random() * 10) + 25,
        ],
        [
          `Week 2`,
          Math.floor(Math.random() * 30) + 90,
          Math.floor(Math.random() * 120) + 250,
          Math.floor(Math.random() * 10) + 28,
        ],
        [
          `Week 3`,
          Math.floor(Math.random() * 35) + 95,
          Math.floor(Math.random() * 140) + 280,
          Math.floor(Math.random() * 10) + 30,
        ],
        [
          `Week 4`,
          Math.floor(Math.random() * 40) + 100,
          Math.floor(Math.random() * 160) + 300,
          Math.floor(Math.random() * 10) + 32,
        ],
      ],
      dailyBreakdown: [
        ["Date", "Day", "Sessions", "Attendance", "Popular Classes"],
        ...Array.from({ length: 10 }, (_, i) => [
          month.moment
            .clone()
            .date(i + 1)
            .format("YYYY-MM-DD"),
          month.moment
            .clone()
            .date(i + 1)
            .format("dddd"),
          Math.floor(Math.random() * 5) + 2,
          Math.floor(Math.random() * 40) + 20,
          ["Yoga", "Pilates", "HIIT", "Barre"][Math.floor(Math.random() * 4)],
        ]),
      ],
      participantTypes: [
        ["Participation Type", "Count", "Percentage"],
        ["One-time Participants", Math.floor(Math.random() * 30) + 60, "47%"],
        [
          "Regular Participants (2-4 sessions)",
          Math.floor(Math.random() * 40) + 70,
          "37%",
        ],
        [
          "Super Users (5+ sessions)",
          Math.floor(Math.random() * 20) + 25,
          "16%",
        ],
      ],
      peakHours: [
        ["Hour", "Sessions", "Avg Attendance"],
        [
          "05:00",
          Math.floor(Math.random() * 50) + 100,
          Math.floor(Math.random() * 10) + 20,
        ],
        [
          "06:00",
          Math.floor(Math.random() * 30) + 50,
          Math.floor(Math.random() * 8) + 15,
        ],
        [
          "07:00",
          Math.floor(Math.random() * 15) + 10,
          Math.floor(Math.random() * 6) + 8,
        ],
        [
          "08:00",
          Math.floor(Math.random() * 20) + 15,
          Math.floor(Math.random() * 8) + 10,
        ],
        [
          "17:00",
          Math.floor(Math.random() * 25) + 20,
          Math.floor(Math.random() * 10) + 12,
        ],
        [
          "18:00",
          Math.floor(Math.random() * 35) + 30,
          Math.floor(Math.random() * 12) + 15,
        ],
      ],
      topClasses: [
        ["Class Name", "Sessions", "Total Attendance", "Avg per Session"],
        [
          "Yoga",
          Math.floor(Math.random() * 15) + 20,
          Math.floor(Math.random() * 200) + 300,
          Math.floor(Math.random() * 8) + 15,
        ],
        [
          "Power Pilates",
          Math.floor(Math.random() * 12) + 15,
          Math.floor(Math.random() * 150) + 250,
          Math.floor(Math.random() * 10) + 18,
        ],
        [
          "Pilates",
          Math.floor(Math.random() * 10) + 12,
          Math.floor(Math.random() * 120) + 200,
          Math.floor(Math.random() * 8) + 16,
        ],
        [
          "Functional Training",
          Math.floor(Math.random() * 8) + 10,
          Math.floor(Math.random() * 100) + 150,
          Math.floor(Math.random() * 6) + 14,
        ],
        [
          "Barre",
          Math.floor(Math.random() * 6) + 8,
          Math.floor(Math.random() * 80) + 120,
          Math.floor(Math.random() * 5) + 12,
        ],
      ],
      topParticipants: [
        ["Employee Name", "Sessions", "Favorite Class", "Streak Days"],
        [
          "Sarah Johnson",
          Math.floor(Math.random() * 5) + 8,
          "Yoga",
          Math.floor(Math.random() * 10) + 15,
        ],
        [
          "Mike Chen",
          Math.floor(Math.random() * 4) + 7,
          "HIIT",
          Math.floor(Math.random() * 8) + 12,
        ],
        [
          "Emma Wilson",
          Math.floor(Math.random() * 4) + 6,
          "Pilates",
          Math.floor(Math.random() * 7) + 10,
        ],
        [
          "Alex Rodriguez",
          Math.floor(Math.random() * 3) + 5,
          "Barre",
          Math.floor(Math.random() * 6) + 8,
        ],
        [
          "Lisa Park",
          Math.floor(Math.random() * 3) + 5,
          "Yoga",
          Math.floor(Math.random() * 5) + 7,
        ],
      ],
    };
  };

  const generateComparisonDataFallback = (months) => {
    return {
      monthlyComparison: [
        ["Month", "Participants", "Sessions", "Check-ins", "Growth %"],
        ...months.map((m) => [
          m.shortName,
          Math.floor(Math.random() * 50) + 150,
          Math.floor(Math.random() * 20) + 65,
          Math.floor(Math.random() * 150) + 400,
          `${Math.floor(Math.random() * 30) + 5}%`,
        ]),
      ],
      trendsComparison: [
        ["Metric", ...months.map((m) => m.shortName)],
        [
          "Unique Participants",
          ...months.map(() => Math.floor(Math.random() * 50) + 150),
        ],
        [
          "Average Frequency",
          ...months.map(() => Number((Math.random() * 1 + 2).toFixed(1))),
        ],
        [
          "Regular Participants",
          ...months.map(() => Math.floor(Math.random() * 30) + 80),
        ],
      ],
      classPopularityComparison: [
        ["Class Name", ...months.map((m) => m.shortName)],
        ["Yoga", ...months.map(() => Math.floor(Math.random() * 100) + 200)],
        [
          "Power Pilates",
          ...months.map(() => Math.floor(Math.random() * 80) + 150),
        ],
        [
          "Functional Training",
          ...months.map(() => Math.floor(Math.random() * 60) + 100),
        ],
        ["Barre", ...months.map(() => Math.floor(Math.random() * 40) + 80)],
      ],
    };
  };

  // ---------- ExcelJS formatting helpers (preserve your layout) ----------
  const setTitleRow = (ws, title, numCols) => {
    ws.mergeCells(1, 1, 1, numCols);
    const c = ws.getCell(1, 1);
    c.value = title;
    c.font = { bold: true, size: 16, color: { argb: "FF2E4A6B" } };
    c.alignment = { horizontal: "center", vertical: "middle" };
    c.fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: "FFE7F3FF" },
    };
  };

  const addTable = (ws, data, startRow = 3, startCol = 1) => {
    // Data
    data.forEach((row, rIdx) => {
      row.forEach((val, cIdx) => {
        const cell = ws.getCell(startRow + rIdx, startCol + cIdx);
        cell.value = val;
        cell.alignment = {
          vertical: "middle",
          horizontal: "center",
          wrapText: true,
        };
        if (rIdx === 0) {
          cell.font = { bold: true, color: { argb: "FFFFFFFF" }, size: 12 };
          cell.fill = {
            type: "pattern",
            pattern: "solid",
            fgColor: { argb: "FF4472C4" },
          };
          cell.border = {
            top: { style: "thick", color: { argb: "FF2E4A6B" } },
            bottom: { style: "thick", color: { argb: "FF2E4A6B" } },
            left: { style: "thin", color: { argb: "FFE0E0E0" } },
            right: { style: "thin", color: { argb: "FFE0E0E0" } },
          };
        } else {
          cell.border = {
            top: { style: "thin", color: { argb: "FFE0E0E0" } },
            bottom: { style: "thin", color: { argb: "FFE0E0E0" } },
            left: { style: "thin", color: { argb: "FFE0E0E0" } },
            right: { style: "thin", color: { argb: "FFE0E0E0" } },
          };
          if (rIdx % 2 === 0) {
            cell.fill = {
              type: "pattern",
              pattern: "solid",
              fgColor: { argb: "FFF8F9FA" },
            };
          }
        }
      });
    });

    // Column widths based on content
    const cols = data[0].length;
    for (let c = 1; c <= cols; c++) {
      let maxLen = 12;
      for (let r = 0; r < data.length; r++) {
        const v = data[r][c - 1];
        maxLen = Math.max(maxLen, String(v ?? "").length);
      }
      ws.getColumn(c).width = Math.min(40, maxLen + 2);
    }

    return {
      lastRow: startRow + data.length - 1,
      lastCol: startCol + data[0].length - 1,
    };
  };

  // ---------- Chart.js rendering to base64 (images we embed with ExcelJS) ----------
  const stripDataUrl = (dataUrl) =>
    dataUrl.replace(/^data:image\/png;base64,/, "");

  const makeChartBase64 = async ({
    type,
    labels,
    datasets,
    width = 800,
    height = 360,
    title,
    showLegend = true,
  }) => {
    const { Chart, registerables } = await import("chart.js");
    Chart.register(...registerables);

    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext("2d");

    // If no colors provided, Chart.js will auto-pick; that's fine.
    const chart = new Chart(ctx, {
      type,
      data: { labels, datasets },
      options: {
        responsive: false,
        animation: false,
        plugins: {
          legend: { display: showLegend },
          title: { display: !!title, text: title },
        },
        scales:
          type === "pie" || type === "doughnut"
            ? {}
            : { x: { ticks: { autoSkip: true } }, y: { beginAtZero: true } },
      },
    });

    const base64 = canvas.toDataURL("image/png");
    chart.destroy();
    return stripDataUrl(base64);
  };

  // Build the “right” chart per sheet (using the actual columns you export)
  const buildChartForSheet = async (sheetKey, monthName, data) => {
    switch (sheetKey) {
      case "summary": {
        // Use first 3 KPI rows for bars
        const rows = data.slice(1, 4);
        const labels = rows.map((r) => r[0]);
        const values = rows.map((r) => Number(r[1]) || 0);
        return makeChartBase64({
          type: "bar",
          labels,
          datasets: [{ label: "Value", data: values }],
          title: `${monthName} Key Metrics`,
        });
      }
      case "weeklyTrends": {
        const rows = data.slice(1);
        const labels = rows.map((r) => r[0]);
        const sessions = rows.map((r) => Number(r[1]));
        const attendance = rows.map((r) => Number(r[2]));
        return makeChartBase64({
          type: "line",
          labels,
          datasets: [
            { label: "Sessions", data: sessions },
            { label: "Attendance", data: attendance },
          ],
          title: `${monthName} Weekly Progress`,
        });
      }
      case "dailyBreakdown": {
        const rows = data.slice(1);
        const labels = rows.map((r) => r[0]);
        const attendance = rows.map((r) => Number(r[3]));
        return makeChartBase64({
          type: "bar",
          labels,
          datasets: [{ label: "Attendance", data: attendance }],
          title: `${monthName} Daily Participation`,
          showLegend: false,
        });
      }
      case "participantTypes": {
        const rows = data.slice(1);
        const labels = rows.map((r) => r[0]);
        const counts = rows.map((r) => Number(r[1]));
        return makeChartBase64({
          type: "pie",
          labels,
          datasets: [{ label: "Participants", data: counts }],
          title: `${monthName} Participant Distribution`,
        });
      }
      case "peakHours": {
        const rows = data.slice(1);
        const labels = rows.map((r) => r[0]);
        const sessions = rows.map((r) => Number(r[1]));
        return makeChartBase64({
          type: "bar",
          labels,
          datasets: [{ label: "Sessions", data: sessions }],
          title: `${monthName} Activity by Hour`,
          showLegend: false,
        });
      }
      case "topClasses": {
        const rows = data.slice(1);
        const labels = rows.map((r) => r[0]);
        const totals = rows.map((r) => Number(r[2])); // Total Attendance
        return makeChartBase64({
          type: "bar",
          labels,
          datasets: [{ label: "Total Attendance", data: totals }],
          title: `${monthName} Most Popular Classes`,
        });
      }
      case "hourlyDailyBreakdown": {
        const rows = data.slice(1);
        const labels = rows.map((r) => `${r[0]} ${r[2]}`); // Date Hour
        const sessions = rows.map((r) => Number(r[3])); // Sessions
        return makeChartBase64({
          type: "bar",
          labels,
          datasets: [{ label: "Sessions", data: sessions }],
          title: `${monthName} Hourly Activity`,
          showLegend: false,
        });
      }
      case "topParticipants": {
        const rows = data.slice(1);
        const labels = rows.map((r) => r[0]);
        const sessions = rows.map((r) => Number(r[1]));
        return makeChartBase64({
          type: "bar",
          labels,
          datasets: [{ label: "Sessions", data: sessions }],
          title: `${monthName} Most Active Participants`,
          showLegend: false,
        });
      }
      case "monthlyComparison": {
        const rows = data.slice(1);
        const labels = rows.map((r) => r[0]);
        const participants = rows.map((r) => Number(r[1]));
        const sessions = rows.map((r) => Number(r[2]));
        const checkins = rows.map((r) => Number(r[3]));
        return makeChartBase64({
          type: "line",
          labels,
          datasets: [
            { label: "Participants", data: participants },
            { label: "Sessions", data: sessions },
            { label: "Check-ins", data: checkins },
          ],
          title: "Multi-Month Trends Comparison",
        });
      }
      case "trendsComparison": {
        // rows: Metric, m1, m2, ...
        const metrics = data.slice(1);
        const labels = data[0].slice(1);
        const datasets = metrics.map((row) => ({
          label: row[0],
          data: row.slice(1).map((n) => Number(n)),
        }));
        return makeChartBase64({
          type: "line",
          labels,
          datasets,
          title: "Key Metrics Trend Analysis",
        });
      }
      case "classPopularityComparison": {
        const rows = data.slice(1);
        const labels = rows.map((r) => r[0]); // class types
        const months = data[0].slice(1);
        // Transpose into datasets per month
        const datasets = months.map((m, i) => ({
          label: m,
          data: rows.map((r) => Number(r[i + 1])),
        }));
        return makeChartBase64({
          type: "bar",
          labels,
          datasets,
          title: "Class Popularity Across Months",
        });
      }
      default:
        return null;
    }
  };

  const placeChartImageRight = (ws, workbook, base64Png, dataCols) => {
    if (!base64Png) return;
    const imgId = workbook.addImage({ base64: base64Png, extension: "png" });

    // Place to the right of the last data column, starting near row 1
    const leftColZeroIndexed = dataCols + 1; // +1 buffer; exceljs image coords are 0-based
    ws.addImage(imgId, {
      tl: { col: leftColZeroIndexed, row: 0.5 },
      ext: { width: 560, height: 320 }, // about 7.2 x 4.4 inches
    });
  };

  const createSheetWithChart = async (
    workbook,
    sheetName,
    tableData,
    chartKey,
    monthName,
  ) => {
    console.log(
      `createSheetWithChart: sheetName='${sheetName}', monthName='${monthName}'`,
    );
    const ws = workbook.addWorksheet(sheetName);

    // Title across table columns
    const numCols = tableData[0].length;
    setTitleRow(
      ws,
      `${companyName}${
        analyticsSite && analyticsSite !== "All Sites"
          ? ` - ${analyticsSite}`
          : ""
      } - ${sheetName}`,
      numCols,
    );

    // Blank row (row 2)
    ws.getRow(2).height = 4;

    // Table starting row 3, preserve ALL columns
    const { lastCol } = addTable(ws, tableData, 3, 1);

    // Make a chart (PNG) and place on right
    const base64 = await buildChartForSheet(chartKey, monthName, tableData);
    placeChartImageRight(ws, workbook, base64, lastCol);
  };

  const createRawDataSheet = (workbook, data) => {
    if (!data) return;

    const ws = workbook.addWorksheet("Raw Data");
    setTitleRow(ws, `${companyName} - Raw Data`, data[0].length);

    // Add the raw data table
    addTable(ws, data, 3, 1);
  };

  const createAllDataSheet = (workbook, monthsData, comparisonData) => {
    const ws = workbook.addWorksheet("All Data");
    setTitleRow(ws, `${companyName} - All Data Summary`, 10);

    let currentRow = 3;

    // Monthly Data
    monthsData.forEach((month, index) => {
      // Add month title
      ws.getCell(currentRow, 1).value = `--- ${month.name} Data ---`;
      ws.getCell(currentRow, 1).font = { bold: true, size: 14 };
      currentRow += 2;

      // Add summary table
      ws.getCell(currentRow, 1).value = "Summary";
      ws.getCell(currentRow, 1).font = { bold: true };
      currentRow++;
      const summaryTableLastRow = addTable(
        ws,
        month.data.summary,
        currentRow,
        1,
      ).lastRow;
      currentRow = summaryTableLastRow + 2;

      // Add other tables as needed
      // You can repeat this pattern for weeklyTrends, dailyBreakdown, etc.
    });

    // Comparison Data
    if (comparisonData) {
      ws.getCell(currentRow, 1).value = `--- Multi-Month Comparison ---`;
      ws.getCell(currentRow, 1).font = { bold: true, size: 14 };
      currentRow += 2;

      // Add monthly comparison table
      ws.getCell(currentRow, 1).value = "Monthly Comparison";
      ws.getCell(currentRow, 1).font = { bold: true };
      currentRow++;
      addTable(ws, comparisonData.monthlyComparison, currentRow, 1);
    }
  };

  // ---------- Export: builds all month sheets + comparison sheets with charts ----------
  async function exportToExcel(monthsDataFromAPI) {
    const startDate = statsExportDates.start;
    const endDate = statsExportDates.end;
    const months = getMonthsInRange(startDate, endDate);
    const isMultiMonth = months.length > 1;

    const workbook = new ExcelJS.Workbook();
    workbook.creator = "Shalibo Wellness Platform";
    workbook.created = new Date();

    const allMonthlyData = [];

    // per-month sheets - use real data from API
    for (let i = 0; i < months.length; i++) {
      const month = months[i];
      const apiData = monthsDataFromAPI[i];

      const monthStart = month.moment.clone().startOf("month");
      const monthEnd = month.moment.clone().endOf("month");
      const segmentStart = startDate.isAfter(monthStart)
        ? startDate
        : monthStart;
      const segmentEnd = endDate.isBefore(monthEnd) ? endDate : monthEnd;
      const isFullMonth =
        segmentStart.isSame(monthStart, "day") &&
        segmentEnd.isSame(monthEnd, "day");

      let name = month.name;
      let shortName = month.shortName;

      if (!isFullMonth) {
        if (segmentStart.format("YYYY-MM") === segmentEnd.format("YYYY-MM")) {
          name = `${segmentStart.format("MMMM D")} – ${segmentEnd.format(
            "D, YYYY",
          )}`;
          shortName = `${segmentStart.format("MMM D")}–${segmentEnd.format(
            "D, YYYY",
          )}`;
        } else {
          name = `${segmentStart.format("MMMM D, YYYY")} – ${segmentEnd.format(
            "MMMM D, YYYY",
          )}`;
          shortName = `${segmentStart.format("MMM D")}–${segmentEnd.format(
            "MMM D, YYYY",
          )}`;
        }
      }

      // Use real data if available, otherwise fallback to sample data
      const monthData =
        apiData && apiData.aggregated
          ? generateMonthlyDataFromAggregated(month, apiData.aggregated)
          : generateMonthlyDataFallback(month);

      allMonthlyData.push({ name: name, data: monthData });

      await createSheetWithChart(
        workbook,
        `${shortName} - Summary`,
        monthData.summary,
        "summary",
        name,
      );
      await createSheetWithChart(
        workbook,
        `${shortName} - Weekly Trends`,
        monthData.weeklyTrends,
        "weeklyTrends",
        name,
      );
      await createSheetWithChart(
        workbook,
        `${shortName} - Daily Activity`,
        monthData.dailyBreakdown,
        "dailyBreakdown",
        name,
      );
      await createSheetWithChart(
        workbook,
        `${shortName} - Hourly Activity`,
        monthData.hourlyDailyBreakdown,
        "hourlyDailyBreakdown",
        name,
      );
      await createSheetWithChart(
        workbook,
        `${shortName} - Participants`,
        monthData.participantTypes,
        "participantTypes",
        name,
      );
      await createSheetWithChart(
        workbook,
        `${shortName} - Peak Hours`,
        monthData.peakHours,
        "peakHours",
        name,
      );
      await createSheetWithChart(
        workbook,
        `${shortName} - Top Classes`,
        monthData.topClasses,
        "topClasses",
        name,
      );
      await createSheetWithChart(
        workbook,
        `${shortName} - Top Users`,
        monthData.topParticipants,
        "topParticipants",
        name,
      );
    }
    let comparisonData = null;
    // comparison sheets
    if (isMultiMonth) {
      // Use real aggregated data if available
      const hasRealData =
        monthsDataFromAPI.length > 0 && monthsDataFromAPI[0]?.aggregated;
      const cmp = hasRealData
        ? generateComparisonData(monthsDataFromAPI)
        : generateComparisonDataFallback(months);
      comparisonData = cmp;

      await createSheetWithChart(
        workbook,
        `📊 Monthly Comparison`,
        cmp.monthlyComparison,
        "monthlyComparison",
        "",
      );

      await createSheetWithChart(
        workbook,
        `📈 Trends Analysis`,
        cmp.trendsComparison,
        "trendsComparison",
        "",
      );

      await createSheetWithChart(
        workbook,
        `🏆 Class Comparison`,
        cmp.classPopularityComparison,
        "classPopularityComparison",
        "",
      );
    }

    // A new sheet for raw data - combine raw data from all months
    const allRawData = monthsDataFromAPI.flatMap((m) => m?.rawData || []);
    const formattedRawData = formatRawDataForExcel(allRawData);
    createRawDataSheet(workbook, formattedRawData);

    // A new sheet with all data combined - Removed as per request (redundant with Summary)
    // createAllDataSheet(workbook, allMonthlyData, comparisonData);

    const isSingleFullMonth =
      !isMultiMonth &&
      startDate.isSame(startDate.clone().startOf("month"), "day") &&
      endDate.isSame(endDate.clone().endOf("month"), "day");

    let dateRangeStr;
    if (isMultiMonth) {
      dateRangeStr = `${startDate.format("YYYY-MM-DD")}_to_${endDate.format(
        "YYYY-MM-DD",
      )}`;
    } else if (isSingleFullMonth) {
      dateRangeStr = startDate.format("MMM-YYYY");
    } else {
      dateRangeStr = `${startDate.format("YYYY-MM-DD")}_to_${endDate.format(
        "YYYY-MM-DD",
      )}`;
    }

    const siteSuffix =
      analyticsSite && analyticsSite !== "All Sites"
        ? `_${analyticsSite.replace(/\s+/g, "_")}`
        : "";
    const timestamp = moment().format("YYYY-MM-DD_HH-mm");
    const fileName = `${companyName}${siteSuffix}_Wellness_Report_${dateRangeStr}_${timestamp}.xlsx`;

    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = fileName;
    a.click();
    URL.revokeObjectURL(url);
  }

  async function handleDetailedReport() {
    if (!currentCompany?._id) {
      openSnackbar("error", "No company selected.");
      return;
    }
    setDetailedGenerating(true);
    setDetailedProgress(0);
    try {
      const { reportId } = await reportsApi.startDetailedReport({
        companyId: currentCompany._id,
        start: statsExportDates.start.toISOString(),
        end: statsExportDates.end.toISOString(),
        site:
          detailedSite && detailedSite !== "All Sites" ? detailedSite : undefined,
      });

      const job = await reportsApi.pollReportStatus(reportId, (tick) => {
        setDetailedProgress(tick.progress);
      });

      if (job.status === "failed") {
        openSnackbar(
          "error",
          `Report generation failed: ${job.error || "unknown error"}`,
        );
        return;
      }

      setDetailedProgress(100);
      const { fileName } = await reportsApi.downloadReport(reportId);
      openSnackbar(
        "success",
        fileName
          ? `Detailed report downloaded: ${fileName}`
          : "Detailed report downloaded.",
      );
    } catch (error) {
      const message =
        error?.response?.data?.error || error?.message || "Unknown error";
      openSnackbar("error", `Could not generate report: ${message}`);
    } finally {
      setDetailedGenerating(false);
      setDetailedProgress(null);
    }
  }

  async function handleMakersStats() {
    const stateStart = statsExportDates.start;
    const stateEnd = statsExportDates.end;
    const months = getMonthsInRange(stateStart, stateEnd);

    try {
      // Fetch data for each month
      const monthsData = [];

      for (const month of months) {
        const monthStart = (
          stateStart.isAfter(month.moment.clone().startOf("month"))
            ? stateStart
            : month.moment.clone().startOf("month")
        ).toISOString();
        const monthEnd = (
          stateEnd.isBefore(month.moment.clone().endOf("month"))
            ? stateEnd
            : month.moment.clone().endOf("month")
        ).toISOString();

        const res = await client.query({
          query: GET_EVENTS_USERS_STATISTICS,
          variables: {
            company: currentCompany._id,
            start: monthStart,
            end: monthEnd,
            site: analyticsSite !== "All Sites" ? analyticsSite : undefined,
          },
          fetchPolicy: "network-only",
        });

        console.log(
          "Fetched data for month:",
          month.moment.format("MMMM YYYY"),
        );

        const statsData = res.data?.eventsUsersStatistics;
        console.log(
          "Raw stats data from API: ===>>",
          JSON.stringify(statsData.stats),
        ); // Log raw stats
        console.log("Stats data length: ===>>", statsData.stats?.length);

        // loggable sample  data
        const data = {
          ...statsData,
          stats: statsData?.stats?.slice(0, 5) || [], // Safely slice or use empty array
        };

        console.log(
          "Sampled Stats data for logging: ===>>",
          JSON.stringify(data),
        );

        if (statsData) {
          let aggregatedData = statsData.aggregated;
          let normalizedStats = normalizeEventUserStats(statsData.stats); // Normalize here

          // Filter by selected site if not "All Sites"
          if (analyticsSite && analyticsSite !== "All Sites") {
            normalizedStats = normalizedStats.filter(
              (s) => s.event_site === analyticsSite,
            );
          }

          const clientAggregatedData =
            aggregateStatsFromRawData(normalizedStats); // Pass normalized data

          // Start with API data, then overlay with client-side calculated data
          // This ensures client-side calculated fields (like hourlyDailyBreakdown) are added if missing from API
          aggregatedData = {
            ...statsData.aggregated, // Start with API data
            ...clientAggregatedData, // Overlay with client-side data, adding missing fields or overwriting if client data is preferred
          };

          monthsData.push({
            month,
            aggregated: aggregatedData, // Use the potentially aggregated data
            rawData: normalizedStats, // Use normalizedStats for rawData as well
          });
        }
      }

      // Export with real data
      await exportToExcel(monthsData);
    } catch (error) {
      console.error("Error fetching statistics:", error);
      // Fallback to sample data if API fails
      await exportToExcel([]);
    }
  }

  const companyName = currentCompany?.name || "Company";
  const companySites = currentCompany?.sites || [];

  const monthsCount = getMonthsInRange(
    statsExportDates.start,
    statsExportDates.end,
  ).length;
  const estimatedSheets = monthsCount * 8 + (monthsCount > 1 ? 3 : 0); // Updated from 7 to 8 sheets per month

  return (
    <div className={classes.grid}>
      <div className={classes.header}>
        <EventMakerDialog action="create" companySites={companySites} />

        {editEvent ? (
          <EventMakerDialog
            action="edit"
            event={editEvent}
            companySites={companySites}
            isOpen
            onClose={() => {
              setEditEvent(null);
            }}
          />
        ) : null}

        <Typography gutterBottom variant="h4" style={{ flex: 1, minWidth: 0, paddingLeft: 240, paddingRight: 340, textAlign: "center" }}>
          Recurring Events Maker ({companyName})
        </Typography>
        <div
          style={{
            position: "absolute",
            right: 0,
            top: 0,
            display: "flex",
            gap: "10px",
          }}
        >
          <Fab
            variant="extended"
            color="secondary"
            onClick={() => setDetailedReportDialogOpen(true)}
          >
            <Add /> Detailed Report
          </Fab>
          <Fab
            variant="extended"
            color="primary"
            onClick={() => setStatsDialogOpen(true)}
          >
            <Add /> Export Analytics
          </Fab>
        </div>
      </div>

      <Grid container spacing={2} style={{ marginBottom: 24, marginTop: 8 }}>
        {eventMakers.length < 1 ? (
          <Paper className={classes.empty}>
            <div style={{ textAlign: "center " }}>
              <CardMedia
                style={{ width: 200, height: 200, margin: "auto" }}
                image={logo}
              />
              <Typography variant="h4" className={classes.emptyText}>
                There are no recurring events
              </Typography>
            </div>
          </Paper>
        ) : (
          eventMakers.map((event, index) => (
            <Grid item key={index} xs={12} sm={6} md={4} lg={4}>
              <EventMakerCard
                onEdit={() => {
                  setEditEvent(event);
                }}
                onClose={() => setEditEvent(null)}
                event={event}
                companySites={companySites}
                title={event.title}
                instructor={event.instructor}
                location={event.location}
                date={event.date}
                totalSpots={event.totalSpots}
                takenSpots={event.users.length}
                description={event.description}
                image={event.image}
                company={event.company}
                hoursTillEvent={event.hoursTillEvent}
                id={event._id}
              />
            </Grid>
          ))
        )}
      </Grid>

      <Dialog
        open={statsDialogOpen}
        onClose={() => setStatsDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle style={{ backgroundColor: "#4472C4", color: "white" }}>
          Export Wellness Analytics
        </DialogTitle>
        <DialogContent style={{ padding: "24px" }}>
          <DialogContentText style={{ marginBottom: "16px" }}>
            Generates monthly sheets with full data preserved and charts
            embedded on the right.
          </DialogContentText>
          <DialogContentText
            style={{ marginBottom: "24px", fontWeight: "bold" }}
          >
            Select Date Range for Analysis
          </DialogContentText>
          <Grid container spacing={3}>
            <Grid item xs={6}>
              <MuiPickersUtilsProvider libInstance={moment} utils={MomentUtils}>
                <DateTimePicker
                  label="Start Date"
                  value={statsExportDates.start}
                  onChange={(date) =>
                    setStatsExportDates({ ...statsExportDates, start: date })
                  }
                  format="YYYY-MM-DD HH:mm"
                  fullWidth
                />
              </MuiPickersUtilsProvider>
            </Grid>
            <Grid item xs={6}>
              <MuiPickersUtilsProvider libInstance={moment} utils={MomentUtils}>
                <DateTimePicker
                  label="End Date"
                  value={statsExportDates.end}
                  onChange={(date) =>
                    setStatsExportDates({ ...statsExportDates, end: date })
                  }
                  format="YYYY-MM-DD HH:mm"
                  fullWidth
                />
              </MuiPickersUtilsProvider>
            </Grid>
          </Grid>

          {companySites && companySites.length > 0 && (
            <FormControl
              variant="outlined"
              style={{ marginTop: "20px", width: "100%" }}
            >
              <InputLabel id="analytics-site-label">Filter by Site</InputLabel>
              <Select
                labelId="analytics-site-label"
                value={analyticsSite}
                onChange={(e) => setAnalyticsSite(e.target.value)}
                label="Filter by Site"
              >
                <MenuItem value="All Sites">All Sites</MenuItem>
                {companySites.map((site) => (
                  <MenuItem key={site} value={site}>
                    {site}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          <div
            style={{
              marginTop: "20px",
              padding: "16px",
              backgroundColor: "#f8f9fa",
              borderRadius: "8px",
              border: "1px solid #dee2e6",
            }}
          >
            <Typography
              variant="body2"
              style={{
                fontWeight: "bold",
                marginBottom: "12px",
                color: "#495057",
              }}
            >
              Report Structure Preview:
            </Typography>
            <Typography variant="body2" style={{ marginBottom: "8px" }}>
              <strong>
                Per Month ({monthsCount} month{monthsCount !== 1 ? "s" : ""}{" "}
                selected):
              </strong>
            </Typography>
            <Typography
              variant="body2"
              style={{ marginLeft: "16px", lineHeight: "1.6" }}
            >
              • Summary (bar chart on right)
              <br />
              • Weekly Trends (two-line chart on right)
              <br />
              • Daily Activity (column chart on right)
              <br />
              • Hourly Activity (bar chart on right)
              <br />
              • Participant Distribution (pie chart on right)
              <br />
              • Peak Hours (column chart on right)
              <br />
              • Top Classes (bar chart on right)
              <br />• Most Active Users (bar chart on right)
            </Typography>

            {monthsCount > 1 && (
              <>
                <Typography
                  variant="body2"
                  style={{ marginTop: "12px", marginBottom: "8px" }}
                >
                  <strong>Comparison Sheets (Multi-month analysis):</strong>
                </Typography>
                <Typography
                  variant="body2"
                  style={{ marginLeft: "16px", lineHeight: "1.6" }}
                >
                  • Monthly Comparison Overview (multi-line chart)
                  <br />
                  • Key Metrics Trends Analysis (multi-line chart)
                  <br />• Class Popularity Comparison (grouped bars)
                </Typography>
              </>
            )}

            <Typography
              variant="caption"
              style={{ marginTop: "12px", display: "block", color: "#6c757d" }}
            >
              Charts are embedded as images to ensure Excel compatibility.
            </Typography>
          </div>
        </DialogContent>
        <DialogActions style={{ padding: "16px 24px" }}>
          <Fab
            variant="extended"
            color="primary"
            onClick={async () => {
              await handleMakersStats();
              setStatsDialogOpen(false);
            }}
            style={{ marginRight: "12px" }}
          >
            <Add /> Generate Report ({estimatedSheets} sheets)
          </Fab>
          <Fab
            variant="extended"
            color="secondary"
            onClick={() => setStatsDialogOpen(false)}
          >
            Cancel
          </Fab>
        </DialogActions>
      </Dialog>

      <Dialog
        open={detailedReportDialogOpen}
        onClose={() => setDetailedReportDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle style={{ backgroundColor: "#00f2c3", color: "white" }}>
          Export Detailed Monthly Report
        </DialogTitle>
        <DialogContent style={{ padding: "24px" }}>
          <DialogContentText style={{ marginBottom: "16px" }}>
            Generates a comprehensive report with separate sheets for Gym
            Subscriptions and Event Attendance for each selected month.
          </DialogContentText>
          <DialogContentText
            style={{ marginBottom: "24px", fontWeight: "bold" }}
          >
            Select Date Range for Detailed Analysis
          </DialogContentText>
          <Grid container spacing={3}>
            <Grid item xs={6}>
              <MuiPickersUtilsProvider libInstance={moment} utils={MomentUtils}>
                <DateTimePicker
                  label="Start Date"
                  value={statsExportDates.start}
                  onChange={(date) =>
                    setStatsExportDates({ ...statsExportDates, start: date })
                  }
                  format="YYYY-MM-DD HH:mm"
                  fullWidth
                />
              </MuiPickersUtilsProvider>
            </Grid>
            <Grid item xs={6}>
              <MuiPickersUtilsProvider libInstance={moment} utils={MomentUtils}>
                <DateTimePicker
                  label="End Date"
                  value={statsExportDates.end}
                  onChange={(date) =>
                    setStatsExportDates({ ...statsExportDates, end: date })
                  }
                  format="YYYY-MM-DD HH:mm"
                  fullWidth
                />
              </MuiPickersUtilsProvider>
            </Grid>
          </Grid>

          {companySites && companySites.length > 0 && (
            <FormControl
              variant="outlined"
              style={{ marginTop: "20px", width: "100%" }}
            >
              <InputLabel id="detailed-site-label">Filter by Site</InputLabel>
              <Select
                labelId="detailed-site-label"
                value={detailedSite}
                onChange={(e) => setDetailedSite(e.target.value)}
                label="Filter by Site"
              >
                <MenuItem value="All Sites">All Sites</MenuItem>
                {companySites.map((site) => (
                  <MenuItem key={site} value={site}>
                    {site}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          <div
            style={{
              marginTop: "20px",
              padding: "16px",
              backgroundColor: "#f8f9fa",
              borderRadius: "8px",
              border: "1px solid #dee2e6",
            }}
          >
            <Typography
              variant="body2"
              style={{
                fontWeight: "bold",
                marginBottom: "12px",
                color: "#495057",
              }}
            >
              Report Components:
            </Typography>
            <Typography variant="body2" style={{ marginBottom: "8px" }}>
              <strong>
                Per Month ({monthsCount} month{monthsCount !== 1 ? "s" : ""}{" "}
                selected):
              </strong>
            </Typography>
            <Typography
              variant="body2"
              style={{ marginLeft: "16px", lineHeight: "1.6" }}
            >
              • <strong>Gym [Month]:</strong> Users with Gym subscription
              (includes subscription type and status columns).
              <br />• <strong>Studio [Month]:</strong> Users with Studio
              subscription with session counts and status columns.
              <br />• <strong>Inactive [Month]:</strong> All non-staff users
              with 0 sessions in the period.
              <br />• <strong>Cancelled [Month]:</strong> Users who cancelled
              their subscription during the period.
              <br />              • <strong>events attendance [Month]:</strong> Tables per
              event with name, date, time and user list with attendance status.
              <br />• <strong>Subscription Switch Logs:</strong> History of all
              subscription switches (Gym &lt;-&gt; Studio).
            </Typography>
            {detailedGenerating && (
              <Typography variant="body2" style={{ marginTop: "12px" }}>
                Generating on the server… {detailedProgress ?? 0}% complete —
                the download starts automatically when it's ready.
              </Typography>
            )}
          </div>
        </DialogContent>
        <DialogActions style={{ padding: "16px 24px" }}>
          <Fab
            variant="extended"
            color="primary"
            disabled={detailedGenerating}
            onClick={async () => {
              try {
                await handleDetailedReport();
                setDetailedReportDialogOpen(false);
              } catch (_err) {
                // Errors are surfaced via Snackbar inside handleDetailedReport;
                // the dialog stays open so the user can retry.
              }
              setDetailedProgress(null);
            }}
            style={{ marginRight: "12px" }}
          >
            <Add /> {detailedGenerating
              ? "Generating…"
              : "Generate Detailed Report"}
          </Fab>
          <Fab
            variant="extended"
            color="secondary"
            disabled={detailedGenerating}
            onClick={() => setDetailedReportDialogOpen(false)}
          >
            Cancel
          </Fab>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default withStyles(styles)(EventMaker);
